import { useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import {
  apiBoostersGetCollection,
  getApiBoostersGetCollectionQueryKey,
  getApiBoostersIdGetQueryOptions
} from '@r/api/generated/client.ts';
import type {
  BoosterBoosterCollectionReadModel,
  BoosterBoosterReadModel,
  PaginatedResponseModel
} from '@r/api/generated/client.schemas.ts';

const ACTIVE_BOOSTER_STATUS = 'booster.status.active';

const ACTIVE_BOOSTERS_ALL_PAGES_QUERY_KEY = [
  ...getApiBoostersGetCollectionQueryKey(),
  'all-pages',
  'active'
] as const;

type BoostersCollectionResponse =
  | BoosterBoosterCollectionReadModel[]
  | {
      data?: BoosterBoosterCollectionReadModel[];
      pagination?: PaginatedResponseModel['pagination'];
    };

/** Normalise la réponse de l'endpoint GET /boosters (tableau ou payload paginé `{ data }`). */
function getBoostersFromResponse(response: BoostersCollectionResponse | undefined) {
  if (!response) {
    return [];
  }

  if (Array.isArray(response)) {
    return response;
  }

  return response.data ?? [];
}

/** Extrait les métadonnées de pagination d'une réponse collection boosters. */
function getBoostersPaginationFromResponse(response: BoostersCollectionResponse | undefined) {
  if (!response || Array.isArray(response)) {
    return null;
  }

  return response.pagination ?? null;
}

/**
 * Récupère les boosters actuellement actifs (statut `booster.status.active`) avec le détail
 * de leurs références éligibles, pour affichage côté participant sur la page programme.
 */
export function useActiveBoosters() {
  const collectionQuery = useQuery({
    queryKey: ACTIVE_BOOSTERS_ALL_PAGES_QUERY_KEY,
    queryFn: async (): Promise<BoosterBoosterCollectionReadModel[]> => {
      const perPage = 30;
      let page = 1;
      let lastPage = 1;
      const boosters: BoosterBoosterCollectionReadModel[] = [];

      do {
        const response = await apiBoostersGetCollection({ page, perPage });
        boosters.push(...getBoostersFromResponse(response));
        lastPage = getBoostersPaginationFromResponse(response)?.last ?? 1;
        page += 1;
      } while (page <= lastPage);

      return boosters;
    },
    placeholderData: (previousData) => previousData
  });

  const activeSummaries = useMemo(
    () =>
      (collectionQuery.data ?? []).filter((booster) => booster.status === ACTIVE_BOOSTER_STATUS),
    [collectionQuery.data]
  );

  const detailQueries = useQueries({
    queries: activeSummaries.map((booster) => getApiBoostersIdGetQueryOptions(String(booster.id)))
  });

  const activeBoosters = useMemo(
    () =>
      detailQueries
        .map((query) => query.data as BoosterBoosterReadModel | undefined)
        .filter((booster): booster is BoosterBoosterReadModel => Boolean(booster)),
    [detailQueries]
  );

  const isLoading = collectionQuery.isLoading || detailQueries.some((query) => query.isLoading);

  return {
    activeBoosters,
    hasActiveBooster: activeSummaries.length > 0,
    isLoading,
    isError: collectionQuery.isError
  };
}
